import { graphql } from 'gatsby';
import React, { useEffect } from 'react';
import queryString from 'query-string';
import { Location } from '@reach/router';
import { queryStringToCookies } from 'utils/urlHelper';
import buildPageComponents from 'utils/buildPageComponents';
import checkQueryParamContraints from 'utils/queryParamConstraints';
import generateBreadcrumbs from 'utils/getBreadcrumbData';
import WebsiteLayout from 'layouts/websiteLayout';

const withReplacement = ComponentToWrap => props => {
  return (
    <Location>
      {({ location }) => {
        // retrieve query params
        const queryParams = location.search ? queryString.parse(location.search) : {};

        // regex: any text between '[]' lowercase a-z
        const regex = /\[[a-z]*\S\]/g;

        // find all instances in page content
        const matches = JSON.stringify(props?.data).match(regex) || [];

        // if query params exist to replace a matching instance, perform replacement.
        let data =
          Object.keys(queryParams).length > 0
            ? JSON.parse(
                Object.keys(queryParams).reduce((prev, cur) => {
                  return prev.replace(
                    `[${cur}]`,
                    checkQueryParamContraints(location.pathname, cur, queryParams[cur])
                  );
                }, JSON.stringify(props?.data))
              )
            : props?.data;

        // if any instances were not included in query params, replace with empty string.
        data = JSON.parse(
          matches.reduce((prev, cur) => {
            return prev.replace(cur, '');
          }, JSON.stringify(data))
        );

        return <ComponentToWrap {...props} data={data} queryParams={queryParams} />;
      }}
    </Location>
  );
};

const WebsitePage = props => {
  const { seo, components } = props?.data?.contentfulPage;

  useEffect(() => {
    queryStringToCookies(props.queryParams);
  }, [props.queryParams]);

  const hero =
    components?.find(ele => ele.__typename === 'ContentfulDynamicHero') ||
    components?.find(ele => ele.__typename === 'ContentfulSection' && ele.isHero) ||
    components?.find(ele => ele.__typename === 'ContentfulLayout' && ele.template.isHero);

  const header = components?.find(
    ele => ele.__typename === 'ContentfulHeader' && !ele.isAnchorMenu
  );
  const hasAnchorLinkNavigation = components?.find(
    ele => ele.__typename === 'ContentfulHeader' && ele.isAnchorMenu
  );
  const footer = components?.find(ele => ele.__typename === 'ContentfulFooter');
  const breadcrumbs = generateBreadcrumbs(seo, hero);

  return (
    <WebsiteLayout
      breadcrumbs={breadcrumbs}
      header={header}
      hero={hero}
      footer={footer}
      hasAnchorLinkNavigation={hasAnchorLinkNavigation}
    >
      {buildPageComponents(props)}
    </WebsiteLayout>
  );
};

export default withReplacement(WebsitePage);

export const WebsitePageQuery = graphql`
  query WebsitePageQuery($pageUrl: String!) {
    contentfulPage(seo: { pageUrl: { eq: $pageUrl } }) {
      ...Page
    }
  }
`;
